import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Wallet, Users, ClipboardCheck } from "lucide-react";

export const Route = createFileRoute("/admin/reports")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Reports — MAKAI" }] }),
  component: ReportsPage,
});

function ReportsPage() {
  const { active } = useSchoolContext();
  const [att, setAtt] = useState<{ date: string; present: number; absent: number }[]>([]);
  const [classBreakdown, setClassBreakdown] = useState<{ name: string; count: number }[]>([]);
  const [fees, setFees] = useState<{ collected: number; outstanding: number; invoices: number; paidInvoices: number }>({ collected: 0, outstanding: 0, invoices: 0, paidInvoices: 0 });

  const load = useCallback(async () => {
    if (!active) return;
    const sid = active.school_id;
    const since = new Date(); since.setDate(since.getDate() - 13);
    const sinceStr = since.toISOString().slice(0,10);

    const [{ data: attRows }, { data: classes }, { data: pay }, { data: inv }, { data: paidInv }] = await Promise.all([
      supabase.from("attendance").select("date, status").eq("school_id", sid).gte("date", sinceStr),
      supabase.from("learners").select("status, current_stream:streams(class:classes(name))").eq("school_id", sid).eq("status","active"),
      supabase.from("payments").select("amount").eq("school_id", sid),
      supabase.from("invoices").select("balance").eq("school_id", sid).in("status",["sent","partial","overdue"]),
      supabase.from("invoices").select("id", { head: true, count: "exact" }).eq("school_id", sid).eq("status","paid"),
    ]);

    const byDay = new Map<string, { present: number; absent: number }>();
    (attRows ?? []).forEach((r) => {
      const cur = byDay.get(r.date) ?? { present: 0, absent: 0 };
      if (r.status === "present" || r.status === "late") cur.present++; else cur.absent++;
      byDay.set(r.date, cur);
    });
    setAtt(Array.from(byDay.entries()).map(([date, v]) => ({ date, ...v })).sort((a,b) => a.date.localeCompare(b.date)));

    const byClass = new Map<string, number>();
    (classes as any[] ?? []).forEach((l) => {
      const k = l.current_stream?.class?.name ?? "Unassigned";
      byClass.set(k, (byClass.get(k) ?? 0) + 1);
    });
    setClassBreakdown(Array.from(byClass.entries()).map(([name, count]) => ({ name, count })).sort((a,b) => a.name.localeCompare(b.name)));

    const collected = (pay ?? []).reduce((s, p) => s + Number(p.amount), 0);
    const outstanding = (inv ?? []).reduce((s, i) => s + Number(i.balance), 0);
    setFees({ collected, outstanding, invoices: (inv?.length ?? 0), paidInvoices: paidInv ? (paidInv as any).count ?? 0 : 0 });
  }, [active]);
  useEffect(() => { load(); }, [load]);

  const ccy = active?.school.currency || "UGX";
  const maxAtt = Math.max(1, ...att.map((a) => a.present + a.absent));
  const maxClass = Math.max(1, ...classBreakdown.map((c) => c.count));

  return (
    <AdminShell title="Reports" subtitle="Snapshot of attendance, enrolment and fees across your school.">
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} k={classBreakdown.reduce((s, c) => s + c.count, 0)} v="Active learners" />
        <Stat icon={ClipboardCheck} k={att.reduce((s, a) => s + a.present, 0)} v="Attendances (14 days)" />
        <Stat icon={Wallet} k={fmt(fees.collected, ccy)} v="Total collected" />
        <Stat icon={TrendingUp} k={fmt(fees.outstanding, ccy)} v="Outstanding" />
      </section>

      <section className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-soft">
        <h2 className="font-display text-lg font-bold">Attendance — last 14 days</h2>
        {att.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No attendance records in this window.</p> : (
          <div className="mt-4 grid grid-cols-7 gap-2 sm:grid-cols-14">
            {att.map((a) => {
              const total = a.present + a.absent;
              const h = Math.max(8, Math.round((total / maxAtt) * 120));
              const pct = total ? Math.round((a.present / total) * 100) : 0;
              return (
                <div key={a.date} className="flex flex-col items-center gap-1">
                  <div className="flex items-end" style={{ height: 130 }}>
                    <div className="w-6 rounded-t bg-primary" style={{ height: h, opacity: 0.4 + pct/200 }} title={`${a.present}/${total} present`} />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{a.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
        <h2 className="font-display text-lg font-bold">Enrolment by class</h2>
        {classBreakdown.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No learners yet.</p> : (
          <div className="mt-4 space-y-2">
            {classBreakdown.map((c) => (
              <div key={c.name}>
                <div className="flex items-center justify-between text-sm"><span className="font-semibold">{c.name}</span><span className="text-muted-foreground">{c.count}</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${(c.count / maxClass) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </AdminShell>
  );
}

function Stat({ icon: Icon, k, v }: { icon: typeof Users; k: React.ReactNode; v: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary-soft text-primary"><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-display text-2xl font-bold">{k}</div>
      <div className="text-xs text-muted-foreground">{v}</div>
    </div>
  );
}
function fmt(n: number, ccy = "UGX") { return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(n); }
